<app-ui-top-loading-bar />

<!-- Facial-recognition gate. It covers the shell from the top layer, so it sits
     outside the rail/content split rather than inside `main`. -->
@if (facialEnrollmentOpen()) {
  <app-ui-facial-enrollment-modal (startRequested)="startFacialEnrollment()" />
}

<!-- Overlay backdrop (tablet expand panel and mobile drawer) -->
@if (shellMode() !== 'desktop') {
  <div
    class="absolute inset-0 bg-black/32 z-150 transition-opacity duration-250 ease-[ease] motion-reduce:transition-none"
    [class.opacity-100]="drawerOpen()"
    [class.opacity-0]="!drawerOpen()"
    [class.pointer-events-auto]="drawerOpen()"
    [class.pointer-events-none]="!drawerOpen()"
    (click)="closeDrawer()"
    aria-hidden="true"
  ></div>
}

<!-- Sidebar -->
<aside
  role="navigation"
  [attr.aria-label]="'NAV.MAIN_MENU' | translate"
  class="shell-aside flex shrink-0 p-3 overflow-visible max-mobile:absolute max-mobile:left-0 max-mobile:top-0 max-mobile:h-full max-mobile:z-200 max-mobile:p-0 max-mobile:transition-[translate,visibility] max-mobile:duration-300 max-mobile:ease-in-out max-mobile:motion-reduce:transition-none max-mobile:-translate-x-full max-mobile:[&.is-open]:translate-x-0 max-mobile:[&.is-open]:transition-[translate]"
  [class.is-open]="drawerOpen()"
  [class.max-mobile:pointer-events-none]="!drawerOpen()"
  [class.max-mobile:invisible]="!drawerOpen()"
>
  <div
    #navPanel
    [id]="navPanelId"
    class="shell-nav-panel group/nav min-h-full shrink-0 flex flex-col overflow-y-auto overflow-x-hidden [&.is-collapsed]:overflow-visible max-mobile:w-[min(312px,85vw)]! max-mobile:h-full max-mobile:bg-(--mat-sys-background) max-mobile:shadow-[4px_0_12px_rgba(0,0,0,0.12)]"
    [class.is-collapsed]="menuCollapsed()"
    [class.is-overlay-open]="tabletExpandExpanded()"
    [attr.role]="overlayModalActive() ? 'dialog' : null"
    [attr.aria-modal]="overlayModalActive() ? 'true' : null"
    [attr.aria-label]="overlayModalActive() ? ('NAV.MAIN_MENU' | translate) : null"
    [attr.tabindex]="overlayModalActive() ? -1 : null"
    [attr.aria-hidden]="shellMode() === 'mobile' && !drawerOpen() ? true : null"
  >
    <!-- Expand / collapse (desktop and tablet rail) -->
    <div class="flex justify-start items-center pt-2 mb-4 max-mobile:hidden">
      <button
        #tabletExpandTrigger
        class="flex items-center justify-center min-w-11 min-h-11 w-11 h-11 rounded-md bg-transparent border-0 appearance-none cursor-pointer text-[#32904A] transition-[background-color] duration-300 ease-in-out motion-reduce:transition-none hover:bg-[#82878D]/10 focus-visible:outline focus-visible:outline-white/80 focus-visible:outline-offset-2"
        type="button"
        (click)="toggleSidebar(tabletExpandTrigger)"
        [attr.aria-label]="toggleLabel() | translate"
        [attr.aria-expanded]="shellMode() === 'tablet' ? drawerOpen() : null"
        [attr.aria-controls]="shellMode() === 'tablet' ? navPanelId : null"
      >
        <img [src]="toggleIconSrc()" alt="" class="h-6 w-6 object-contain" aria-hidden="true" />
      </button>
    </div>

    <!-- Mobile drawer close -->
    <div class="hidden max-mobile:flex justify-end items-center pt-2 px-2 mb-2 shrink-0">
      <button
        class="flex items-center justify-center min-w-11 min-h-11 w-11 h-11 rounded-md bg-transparent border-0 appearance-none cursor-pointer text-[#32904A] transition-[background-color] duration-200 ease-[ease] motion-reduce:transition-none hover:bg-[#82878D]/10 focus-visible:outline focus-visible:outline-[#32904A] focus-visible:outline-offset-2"
        type="button"
        (click)="closeDrawer()"
        [attr.aria-label]="'NAV.CLOSE' | translate"
      >
        <ng-icon name="heroXMark" class="text-[24px]" aria-hidden="true" />
      </button>
    </div>

    <!-- Brand. One image: the orbe is the left square of `logo.svg`, so the
         collapsed rail clips the logo to that square and expanding widens the
         clip - the wordmark is revealed with the panel, never cross-faded. -->
    <div
      class="flex shrink-0 justify-center transition-[height] duration-300 ease-in-out motion-reduce:transition-none"
      [style.height.px]="menuCollapsed() ? 74 : 140"
    >
      <a
        class="block shrink-0 overflow-hidden rounded-md transition-[width,height,margin-top] duration-300 ease-in-out motion-reduce:transition-none focus-visible:outline focus-visible:outline-[#32904A] focus-visible:outline-offset-2"
        routerLink="/dashboard"
        [attr.aria-label]="'NAV.HOME' | translate"
        [style.width.px]="menuCollapsed() ? 87 : 240"
        [style.height.px]="menuCollapsed() ? 87 : 116"
        [style.margin-top.px]="menuCollapsed() ? -14 : 8"
        (click)="closeDrawer()"
      >
        <img class="h-full w-auto max-w-none" src="assets/images/logo.svg" alt="Veredis" />
      </a>
    </div>

    <!-- Navigation items -->
    <app-menu [collapsed]="menuCollapsed()" (itemSelected)="closeDrawer()" />
  </div>
</aside>

<!-- Content area: Topbar (mobile) + main -->
<!-- `min-h-0`: without it this flex child refuses to shrink below its content and
     `main` never becomes the scroller, however tall the page gets. -->
<div
  class="flex flex-col flex-1 min-w-0 min-h-0 overflow-hidden"
  [attr.inert]="overlayModalActive() ? '' : null"
>
  <!-- Topbar (mobile only) -->
  <!-- `z-110` keeps the avatar menu over the page header (`sticky z-100` in `main`) and
       under the drawer backdrop (`z-150`). -->
  <header class="hidden max-mobile:flex items-center h-13 px-3 gap-2 text-white shrink-0 z-110">
    <button
      #mobileMenuTrigger
      class="flex items-center justify-center min-w-11 min-h-11 w-11 h-11 rounded-full bg-transparent border-0 appearance-none cursor-pointer text-black shrink-0 transition-[background-color] duration-200 ease-[ease] motion-reduce:transition-none hover:bg-white/15 focus-visible:outline focus-visible:outline-white/80 focus-visible:outline-offset-2"
      type="button"
      (click)="toggleDrawer()"
      [attr.aria-label]="'NAV.OPEN' | translate"
      [attr.aria-expanded]="drawerOpen()"
      [attr.aria-controls]="navPanelId"
    >
      <ng-icon name="heroBars3" class="text-[24px]" aria-hidden="true" />
    </button>
    <!-- The header's own actions, lifted beside the drawer button so the page header
         keeps only the title block (product decision 2026-10-26). -->
    <div class="ml-auto text-black">
      <app-dashboard-header [actionsOnly]="true" />
    </div>
  </header>

  <!-- `pl-4.5` (18px) plus the rail's own `p-3` (12px) makes the left gutter the
       same 30px as `pr-7.5` on the right; the content used to sit 12px from the
       rail and 30px from the window edge. Below the mobile breakpoint there is no
       rail to add to, so both sides take the topbar's 12px (`px-3`) instead of an
       uneven 18px / 30px. -->
  <main class="flex-1 min-h-0 overflow-y-auto pl-4.5 pr-7.5 pb-7.5 max-mobile:pl-3 max-mobile:pr-3">
    <!-- Ceiling on the reading width. It does nothing at 1920 with the rail open
         (the column measures 1546px), and takes hold once the rail collapses,
         where the row would otherwise stretch past 1780px and the exposures
         table would be five columns separated by empty space. -->
    <div class="mx-auto w-full max-w-[100rem]">
      <app-dashboard-header />
      <router-outlet />
    </div>
  </main>
</div>
